import { type ShiftInviteStatus } from "@clipboard-health/contract-backend-main";
import { isDefined } from "@clipboard-health/util-ts";
import { type CoreMessageType } from "@sendbird/uikit-react/types/utils";
import { isEmpty } from "lodash";

import { APP_V2_APP_EVENTS, logError, logEvent } from "../lib/analytics";
import { type ShiftTimeProposalStatus } from "../Shifts/ShiftTimeProposals/types";
import {
  CustomMessageTypes,
  type ShiftInviteMessageProps,
  type VisitInviteMessageProps,
} from "./type";

function hasAgentString(message: unknown): message is { agent: string } {
  return typeof message === "object" && isDefined(message) && "agent" in message;
}

function hasShiftInviteData(
  data: unknown
): data is { shiftInviteId: string; status: ShiftInviteStatus } {
  return typeof data === "object" && isDefined(data) && "shiftInviteId" in data && "status" in data;
}

function hasVisitData(data: unknown): data is { visitId: string; caseId: string } {
  return typeof data === "object" && isDefined(data) && "visitId" in data && "caseId" in data;
}

export function parseAgentChatMessage(message: CoreMessageType) {
  try {
    const hasMessage = "message" in message && typeof message.message === "string";
    if (!hasMessage) {
      return undefined;
    }

    if (isValidJson(message.message)) {
      const parsedMessage: unknown = JSON.parse(message.message);
      return hasAgentString(parsedMessage) ? parsedMessage.agent : undefined;
    }

    return message.message;
  } catch (error) {
    logError("Failed to parse agent message from chat message", {
      error,
      metadata: { message },
    });
    return undefined;
  }
}

export function parseShiftInviteChatMessage(props: ShiftInviteMessageProps) {
  const { message } = props;
  const defaultData = { shiftInviteId: undefined, status: undefined };

  try {
    const agentMessage = parseAgentChatMessage(message);

    const hasData = isDefined(message.data) && !isEmpty(message.data);
    if (!hasData || !isValidJson(message.data)) {
      return { agentMessage, ...defaultData };
    }

    const parsedData: unknown = JSON.parse(message.data);
    if (!hasShiftInviteData(parsedData)) {
      return { agentMessage, ...defaultData };
    }

    return { agentMessage, ...parsedData };
  } catch (error) {
    logError("Shift Invite Chat Message Parsing Error", { error, metadata: { message } });
    return { agentMessage: undefined, ...defaultData };
  }
}

export function parseVisitInviteChatMessage(props: VisitInviteMessageProps): {
  visitId: string | undefined;
  caseId: string | undefined;
} {
  const { message } = props;
  const defaultData = { visitId: undefined, caseId: undefined };

  try {
    const hasData = isDefined(message.data) && !isEmpty(message.data);
    if (!hasData || !isValidJson(message.data)) {
      return { ...defaultData };
    }

    const parsedData: unknown = JSON.parse(message.data);
    if (!hasVisitData(parsedData)) {
      return { ...defaultData };
    }

    return { ...parsedData };
  } catch (error) {
    logError("Visit Invite Chat Message Parsing Error", { error, metadata: { message } });
    return { ...defaultData };
  }
}

export function shiftTimeProposalDisplayMessage(chatType: CustomMessageTypes) {
  if (
    [
      CustomMessageTypes.SHIFT_TIME_PROPOSAL_CREATED,
      CustomMessageTypes.SHIFT_TIME_PROPOSAL_UPDATED,
    ].includes(chatType)
  ) {
    return "Proposed a new time for your shift";
  }

  if (chatType === CustomMessageTypes.SHIFT_TIME_PROPOSAL_REPLIED) {
    return "You replied to the shift time proposal";
  }

  return undefined;
}

interface GetShiftTimeProposalChatButtonLabelProps {
  status: ShiftTimeProposalStatus;
  isProposalExpired: boolean;
}

export function getShiftTimeProposalChatButtonLabel({
  status,
  isProposalExpired,
}: GetShiftTimeProposalChatButtonLabelProps) {
  if (status === "PROPOSED" && isProposalExpired) {
    return "Expired";
  }

  // FIXME: Automatically disabled during ESLint config update
  // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check
  switch (status) {
    case "ACCEPTED":
    case "APPLIED": {
      return "Accepted!";
    }

    case "DECLINED": {
      return "Declined";
    }

    default: {
      return "Unavailable";
    }
  }
}

export function isValidJson(message: string) {
  try {
    JSON.parse(message);
    return true;
  } catch {
    return false;
  }
}

export function isGroupShiftInviteMessage(message: CoreMessageType): boolean {
  if (message.customType === CustomMessageTypes.GROUP_SHIFT_INVITE_REQUEST) {
    return true;
  }

  // Handle legacy SHIFT_INVITE_REQUEST messages that are actually group invites
  // (they don't have shiftInviteId in message.data)
  // This will be removed when all group messages have been migrated to GROUP_SHIFT_INVITE_REQUEST
  if (message.customType === CustomMessageTypes.SHIFT_INVITE_REQUEST) {
    const { shiftInviteId, status } = parseShiftInviteChatMessage({ message });
    if (!isDefined(shiftInviteId) || !isDefined(status)) {
      logEvent(APP_V2_APP_EVENTS.SHIFT_INVITE_REQUEST_CLASSIFIED_AS_GROUP_INVITE, {
        messageId: String(message.messageId),
        customType: message.customType ?? undefined,
      });
      return true;
    }
    return false;
  }

  return false;
}
